import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type Team,
  type TournamentDetail,
  type TournamentEntry,
  type TournamentMatch,
  type Venue,
} from '../api';
import { AppShell } from '../components/AppShell';

export function TournamentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [t, setT] = useState<TournamentDetail | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!id) return;
    try {
      setErr(null);
      const [td, ts, vs] = await Promise.all([
        api.getTournament(id),
        api.listTeams(),
        api.listVenues(),
      ]);
      setT(td);
      setTeams(ts);
      setVenues(vs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function addTeam(teamId: string) {
    if (!id) return;
    setBusy(true);
    try {
      await api.addTournamentEntry(id, { teamId });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function removeEntry(entryId: string) {
    if (!id) return;
    setBusy(true);
    try {
      await api.removeTournamentEntry(id, entryId);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    if (!id || !t) return;
    if (!confirm(`Generate bracket for ${t.entries.length} teams? This locks the entry list.`)) return;
    setBusy(true);
    try {
      const seedOrder = t.entries.map((e) => e.teamId);
      await api.generateBracket(id, seedOrder);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'generate_failed');
    } finally {
      setBusy(false);
    }
  }

  async function scheduleMatch(matchId: string) {
    if (!t) return;
    const venueId = venues[0]?.id;
    if (!venueId) {
      alert('Add a venue first.');
      return;
    }
    setBusy(true);
    try {
      const { gameId } = await api.scheduleTournamentMatch(matchId, venueId);
      navigate(`/games/${gameId}`);
    } finally {
      setBusy(false);
    }
  }

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!t) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  const entryTeamIds = new Set(t.entries.map((e) => e.teamId));
  const availableTeams = teams.filter((tm) => !entryTeamIds.has(tm.id));
  const isLocked = t.status === 'LIVE' || t.status === 'COMPLETED';

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Link to="/tournaments" className="text-sm text-muted hover:text-text">
            ← Tournaments
          </Link>
          <div className="mt-2 flex items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t.name}</h1>
              <p className="mt-1 text-muted">
                {t.sport.name} · {t.format.replace(/_/g, ' ').toLowerCase()} ·{' '}
                {t.status}
              </p>
            </div>
            {!isLocked && t.entries.length >= 2 && (
              <button
                className="btn btn-primary"
                onClick={generate}
                disabled={busy}
              >
                Generate bracket ({t.entries.length} teams)
              </button>
            )}
          </div>
        </div>

        {/* Entries section */}
        {!isLocked && (
          <div className="card space-y-3">
            <div className="text-xs uppercase tracking-wider text-muted">
              Teams ({t.entries.length})
            </div>
            {t.entries.length === 0 ? (
              <p className="text-sm text-muted">No teams added yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {t.entries.map((e) => (
                  <EntryRow key={e.id} entry={e} onRemove={() => removeEntry(e.id)} disabled={busy} />
                ))}
              </ul>
            )}
            {availableTeams.length > 0 && (
              <div className="border-t border-border pt-3">
                <div className="mb-1 text-xs uppercase tracking-wider text-muted">
                  Add a team
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {availableTeams.map((tm) => (
                    <button
                      key={tm.id}
                      type="button"
                      onClick={() => addTeam(tm.id)}
                      disabled={busy}
                      className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-semibold text-muted hover:border-brand hover:text-text disabled:opacity-50"
                    >
                      <span
                        className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                        style={{ background: tm.colorPrimary ?? '#3b82f6' }}
                      />
                      {tm.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bracket / standings */}
        {t.matches.length > 0 &&
          (t.format === 'ROUND_ROBIN' ? (
            <RoundRobinView t={t} onSchedule={scheduleMatch} busy={busy} />
          ) : (
            <SingleElimView t={t} onSchedule={scheduleMatch} busy={busy} />
          ))}
      </div>
    </AppShell>
  );
}

function EntryRow({
  entry,
  onRemove,
  disabled,
}: {
  entry: TournamentEntry;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm">
      <div className="flex items-center gap-3">
        <span className="w-6 font-mono text-muted">#{entry.seed}</span>
        <span
          className="h-3 w-3 rounded-full"
          style={{ background: entry.team.colorPrimary ?? '#3b82f6' }}
        />
        <span className="font-semibold">{entry.team.name}</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-xs text-red-300 hover:underline disabled:opacity-50"
      >
        Remove
      </button>
    </li>
  );
}

function SingleElimView({
  t,
  onSchedule,
  busy,
}: {
  t: TournamentDetail;
  onSchedule: (matchId: string) => void;
  busy: boolean;
}) {
  const rounds = useMemo(() => {
    const byRound = new Map<number, TournamentMatch[]>();
    for (const m of t.matches) {
      if (!byRound.has(m.round)) byRound.set(m.round, []);
      byRound.get(m.round)!.push(m);
    }
    return [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  }, [t.matches]);

  return (
    <div className="card overflow-x-auto">
      <div className="text-xs uppercase tracking-wider text-muted">Bracket</div>
      <div className="mt-3 flex gap-8">
        {rounds.map(([roundIdx, matches]) => (
          <div
            key={roundIdx}
            className="flex flex-col justify-around gap-4"
            style={{ minWidth: 220 }}
          >
            <div className="text-[10px] uppercase tracking-wider text-muted">
              Round {roundIdx + 1}
            </div>
            {matches.map((m) => (
              <MatchCard key={m.id} match={m} onSchedule={onSchedule} busy={busy} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function RoundRobinView({
  t,
  onSchedule,
  busy,
}: {
  t: TournamentDetail;
  onSchedule: (matchId: string) => void;
  busy: boolean;
}) {
  // Compute standings from FINAL matches.
  const standings = useMemo(() => {
    const rows = new Map<
      string,
      { team: { id: string; name: string }; w: number; l: number }
    >();
    for (const e of t.entries) {
      rows.set(e.id, { team: e.team, w: 0, l: 0 });
    }
    for (const m of t.matches) {
      if (m.status !== 'FINAL' || !m.winnerEntry) continue;
      const winId = m.winnerEntry.id;
      const loser =
        m.homeEntry && m.homeEntry.id === winId ? m.awayEntry : m.homeEntry;
      const winRow = rows.get(winId);
      if (winRow) winRow.w += 1;
      if (loser) {
        const lr = rows.get(loser.id);
        if (lr) lr.l += 1;
      }
    }
    return [...rows.values()].sort((a, b) =>
      a.w !== b.w ? b.w - a.w : a.team.name.localeCompare(b.team.name),
    );
  }, [t.matches, t.entries]);

  return (
    <>
      <div className="card">
        <div className="text-xs uppercase tracking-wider text-muted">
          Standings
        </div>
        <table className="mt-2 w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted">
            <tr>
              <th className="px-2 py-1 text-left">Team</th>
              <th className="px-2 py-1 text-right">W</th>
              <th className="px-2 py-1 text-right">L</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {standings.map((row) => (
              <tr key={row.team.id}>
                <td className="px-2 py-1">{row.team.name}</td>
                <td className="px-2 py-1 text-right font-mono">{row.w}</td>
                <td className="px-2 py-1 text-right font-mono">{row.l}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card space-y-2">
        <div className="text-xs uppercase tracking-wider text-muted">
          Matches
        </div>
        {t.matches.map((m) => (
          <MatchCard key={m.id} match={m} onSchedule={onSchedule} busy={busy} />
        ))}
      </div>
    </>
  );
}

function MatchCard({
  match,
  onSchedule,
  busy,
}: {
  match: TournamentMatch;
  onSchedule: (matchId: string) => void;
  busy: boolean;
}) {
  return (
    <div
      className={`rounded-md border p-3 text-sm ${
        match.status === 'FINAL'
          ? 'border-white/10 bg-white/[0.02]'
          : match.status === 'LIVE'
          ? 'border-emerald-400/40 bg-emerald-400/5'
          : match.status === 'READY'
          ? 'border-amber-400/40 bg-amber-400/5'
          : 'border-border bg-surface'
      }`}
    >
      <SlotLine
        entry={match.homeEntry}
        score={match.game?.currentState?.home?.score}
        won={match.winnerEntry?.id === match.homeEntry?.id}
      />
      <SlotLine
        entry={match.awayEntry}
        score={match.game?.currentState?.away?.score}
        won={match.winnerEntry?.id === match.awayEntry?.id}
      />
      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-muted">
        <span>{match.status}</span>
        {match.status === 'READY' && (
          <button
            type="button"
            onClick={() => onSchedule(match.id)}
            disabled={busy}
            className="text-[11px] font-semibold text-brand hover:underline"
          >
            Schedule →
          </button>
        )}
        {match.gameId && (
          <Link
            to={`/games/${match.gameId}`}
            className="text-[11px] font-semibold text-brand hover:underline"
          >
            Open game →
          </Link>
        )}
      </div>
    </div>
  );
}

function SlotLine({
  entry,
  score,
  won,
}: {
  entry: TournamentEntry | null;
  score: number | undefined;
  won: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-2 ${
        won ? 'text-text' : entry ? 'text-muted' : 'text-muted/40 italic'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {entry && (
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ background: entry.team.colorPrimary ?? '#3b82f6' }}
          />
        )}
        <span className="truncate text-sm font-semibold">
          {entry ? entry.team.name : 'TBD'}
        </span>
        {entry && (
          <span className="font-mono text-xs text-muted">#{entry.seed}</span>
        )}
      </div>
      <span className="font-mono text-sm">
        {typeof score === 'number' ? score : '—'}
      </span>
    </div>
  );
}
